<template>
    <Modal
        :isOpen="isOpen"
        ref="modal"
        size="xl"
        :ok-disabled="false"
        :cancel-disabled="false"
        :no-close-on-esc="true"
        :no-close-on-backdrop="true"
        :hide-header-close="false"
        :is-modal-route="false"
        centered
        hide-footer
        :title="$t('nBox.nBoxCalibration')"
        @hideHandler="$emit('cancel')"
    >
        <div class="calibration">
            <div :key="user.id.$model" v-for="user in $v.users.$each.$iter">
                <div class="d-flex flex-column flex-xl-row mb-3">
                    <div class="mr-2 mb-2">
                        <div class="user">
                            <div class="user__avatar">
                                <avatar
                                    :size="48"
                                    :src="user.picture.$model"
                                    :username="user.name.$model"
                                />
                            </div>
                            <div>
                                <div class="user__name">
                                    {{ user.name.$model }}
                                </div>
                                <div class="user__occupation">
                                    {{ user.occupation.$model }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="nBox.calibration_type == 'quadrant'"
                        class="d-flex flex-column flex-md-row"
                    >
                        <ActualValue
                            class="actual_quadrant mr-5 mb-2"
                            :calibrationType="nBox.calibration_type"
                            :value="{
                                x: user.actual_x.$model,
                                y: user.actual_y.$model,
                                quadrant: user.quadrant.$model,
                            }"
                        />
                        <div class="mb-2">
                            <QuadrantCalibration
                                :quadrants="quadrants"
                                :xValue.sync="user.x.$model"
                                :yValue.sync="user.y.$model"
                                :selected.sync="user.newQuadrant.$model"
                                :state="
                                    checkState(user.x) && checkState(user.y)
                                "
                            />
                        </div>
                    </div>
                    <div v-else class="d-flex flex-column flex-md-row">
                        <ActualValue
                            class="actual_score mr-5 mb-2"
                            :calibrationType="nBox.calibration_type"
                            :value="{
                                x: user.actual_x.$model,
                                y: user.actual_y.$model,
                                quadrant: user.quadrant.$model,
                            }"
                        />
                        <ScoreCalibration
                            class="d-flex"
                            :xValue.sync="user.x.$model"
                            :yValue.sync="user.y.$model"
                            :state="{
                                x: checkState(user.x),
                                y: checkState(user.y),
                            }"
                        />
                    </div>
                </div>
            </div>
            <div class="d-flex justify-content-end">
                <b-button size="sm" class="mr-2" @click="$emit('cancel')">{{
                    $t('form.button.cancel')
                }}</b-button>
                <b-button size="sm" variant="primary" @click="onSubmit">{{
                    $t('form.button.save')
                }}</b-button>
            </div>
        </div>
    </Modal>
</template>
<script>
import i18n from '@/i18n'
import Avatar from 'vue-avatar'
import { calibrateUsers } from './api'
import ActualValue from '../actual-value'
import { validationMixin } from 'vuelidate'
import validations from './validations'
import Modal from '@/views/components/modal'
import { proceedWithConfirmation } from '@/lib/sweet-alert'
import notification, { success, danger } from '@/lib/notification'
import ScoreCalibration from '@/views/n-box/components/score-calibration'
import QuadrantCalibration from '@/views/n-box/components/quadrant-calibration'

notification.init()

export default {
    components: {
        Modal,
        Avatar,
        ActualValue,
        ScoreCalibration,
        QuadrantCalibration,
    },
    mixins: [validationMixin],
    props: {
        nBox: {
            type: Object,
            required: true,
        },
        data: {
            type: Array,
            required: true,
        },
        quadrants: {
            type: Array,
            required: true,
        },
        isOpen: {
            type: Boolean,
            required: true,
        },
    },
    watch: {
        isOpen(newValue) {
            if (!newValue) {
                return
            }

            this.setUsers()
            this.$v.$reset()
        },
    },
    data() {
        return {
            users: [],
            isLoading: false,
        }
    },
    validations: {
        users: {
            ...validations,
        },
    },
    methods: {
        setUsers() {
            this.users = this.data.map(user => ({
                ...user,
                x: null,
                y: null,
                newQuadrant: null,
                actual_x: user.calibrated.x,
                actual_y: user.calibrated.y,
            }))
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            const payload = this.getCalibrationPayload()
            proceedWithConfirmation({
                message: i18n.t('notification.usersCalibration'),
                onConfirm: () => calibrateUsers(this.nBox.id, payload),
                callback: () => {
                    success(i18n.t('notification.usersCalibrationSuccess'))
                    this.$emit('updated')
                },
                onError: () => danger(),
            })
        },
        getCalibrationPayload() {
            return this.users.map(user => ({
                user_id: user.id,
                x_score: user.x,
                y_score: user.y,
            }))
        },
        checkState(item) {
            return item.$dirty ? !item.$invalid : null
        },
        validate() {
            this.$v.$touch()
            return !this.$v.$invalid
        },
    },
}
</script>
<style lang="scss" scoped>
$default-max-width: 350px;
$default-color: #828282;
$default-border: solid 1px $default-color;

.calibration {
    padding: 10px;
}

.calibration__title {
    color: $default-color;
}

.user {
    display: flex;
    align-items: center;
    height: 100%;
    width: $default-max-width;
    color: #6e6e6e;
}

.user__avatar {
    margin-right: 15px;
}

.user__name {
    font-size: 15px;
    font-weight: 700;
}

.user__occupation {
    font-size: 13px;
}

.actual_quadrant {
    width: $default-max-width;
}

.actual_score {
    width: $default-max-width;
}

::v-deep .label {
    display: flex;
}

::v-deep .container-input-x {
    margin-right: 20px;
}

::v-deep .input-x {
    width: 175px !important;
}

::v-deep .input-y {
    width: 175px !important;
}

::v-deep .form-control {
    border: $default-border;
}

::v-deep .custom-select {
    width: $default-max-width;
    border: $default-border;
}

::v-deep .is-valid,
.custom-select.is-valid {
    border-color: #4dbd74;
}

::v-deep .is-invalid,
.custom-select.is-invalid {
    border-color: #f86c6b;
}

::v-deep .form-group {
    color: $default-color;
}
</style>
